import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { UserProvider } from './contexts/UserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { GlobalProfileProvider } from './contexts/GlobalProfileContext';
import { PhotoProvider } from './contexts/PhotoContext';
// V212.3: StatusBar initialization for Android
import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { syncStatusBarToTheme } from '@/utils/capacitor';
import { shouldSkipNativeSystemBarManipulation } from '@/utils/androidPlatform';
import { App as CapApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
// V209.10: Heavy providers moved to MainAppShell (lazy loaded) - not imported here to prevent bundling in /auth
import { useLastActive } from './hooks/useLastActive';
// V213.1: Android back button hook with priority system
import { initializeAndroidBackButton } from './hooks/useAndroidBackButton';
// V209.10: TrustScoreService removed from initial bundle - only used in NetworkDataContext (lazy loaded)
import { supabase } from './lib/supabaseGuard';
// V210.5: Subscription protection hook
import { useSubscriptionProtection } from './features/subscription/hooks/useSubscriptionProtection';
// V291.2: Entitlements hook for app resume refresh
import { useEntitlements } from './hooks/useEntitlements';
import { useBilling } from './hooks/useBilling';
import { AppBootSplash } from '@/components/AppBootSplash';
import { useDocumentDirection } from '@/i18n/useDocumentDirection';
import { PreferenceSyncOnAuth } from '@/components/PreferenceSyncOnAuth';
import { debugInfo, debugLog } from '@/utils/logger';
import { signalBootReady } from '@/utils/bootReady';
import { logAuthEvent, resolveAuthRouteDecision } from '@/utils/authSessionGuard';
import { claimInviteDeepLinkNavigation } from '@/utils/inviteDeepLinkConsume';
import { useNetworkRecovery } from '@/hooks/useNetworkRecovery';
import { recoverMessagesChannel } from '@/realtime/messagesChannel';

// P0: ensure-user-row + profile bootstrap lives in useAuth.tsx only (single path).
// V206.3: Already imported above
// import { SplashProvider, useSplash } from './contexts/SplashContext'; // V151.17: Using global approach instead
import NavigationGuard from './components/NavigationGuard';
import BottomNavigation from './components/BottomNavigation';
import { TopNavigation } from './components/TopNavigation';
import { KeepAliveTabs } from './components/KeepAliveTabs';

// 🚀 V208.P.1: Critical imports (loaded immediately)
import AuthenticationStep from './components/onboarding/AuthenticationStep';
import OnboardingContainer from './components/onboarding/OnboardingContainer';
import AuthCallback from './pages/AuthCallback';

// V209.10: Lazy load main app routes to reduce /auth bundle size
const Profile = lazy(() => import('./pages/Profile'));
const Discover = lazy(() => import('./pages/Discover'));
const Network = lazy(() => import('./pages/Network'));
const ChatList = lazy(() => import('./pages/ChatList'));
const Settings = lazy(() => import('./pages/Settings'));
const Landing = lazy(() => import('./pages/Landing'));

// V209.10: Lazy load MainAppShell to prevent heavy providers from bundling in /auth
// AuthShell is simple enough to import directly
import { AuthShell } from './shells/AuthShell';
const MainAppShell = lazy(() => import('./shells/MainAppShell').then(mod => ({ default: mod.MainAppShell })));

// 🚀 V208.P.1: Lazy load non-critical screens for faster startup
const PhotoUploadPage = lazy(() => import('./pages/PhotoUploadPage'));
const ChatConversation = lazy(() => import('./pages/ChatConversation'));
const InviteAccept = lazy(() => import('./pages/InviteAccept'));
const InviteRedirect = lazy(() => import('./pages/InviteRedirect'));
const BarIlanAdminPage = lazy(() => import('./pages/BarIlanAdminPage'));

const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TestLegalConsent = lazy(() => import('./pages/TestLegalConsent'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
// V181.1: Public legal pages - lazy loaded
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const RefundPage = lazy(() => import('./pages/legal/RefundPage'));
// V181.2.2: Add Community Guidelines public page
const GuidelinesPage = lazy(() => import('./pages/legal/GuidelinesPage'));
// V181.3: Add Wedding Gift Program public page
const WeddingGiftPage = lazy(() => import('./pages/legal/WeddingGiftPage'));
// V181.4: Add Pricing & Plans public page
const PricingPage = lazy(() => import('./pages/legal/PricingPage'));
// V206.5: Add Safety & Moderation Policy public page
const SafetyPage = lazy(() => import('./pages/legal/SafetyPage'));
// V207.1: Add Subscription Management public page
const SubscriptionManagementPage = lazy(() => import('./pages/legal/SubscriptionManagementPage').then(m => ({ default: m.SubscriptionManagementPage })));
// V202: Add Contact page
const ContactPage = lazy(() => import('./pages/legal/ContactPage'));
// 🎯 V184.1: TASK 4 - Safety & Reporting help page
const SafetyHelpPage = lazy(() => import('./pages/help/SafetyPage'));
const ProfileDirect = lazy(() => import('./pages/ProfileDirect'));
const InternalDashboard = lazy(() => import('./pages/InternalDashboard'));
const VerificationSuccess = lazy(() => import('./pages/VerificationSuccess'));
const VerificationCancel = lazy(() => import('./pages/VerificationCancel'));
// V187.5: Test page for dialog verification
const TestDialog = lazy(() => import('./pages/TestDialog'));
// V204: Goodbye page for account deletion
const Goodbye = lazy(() => import('./pages/Goodbye'));
// V204: Delete request support page for Google Play compliance
const DeleteRequest = lazy(() => import('./pages/support/DeleteRequest'));
// Public support page for App Store Connect submission
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
// Public about page for Google OAuth Branding review
const AboutPage = lazy(() => import('./pages/AboutPage'));
// Bar-Ilan Founding admin-only dashboard (read-only, code-only admin allowlist)
const BarIlanDashboardPage = lazy(() => import('./pages/BarIlanDashboardPage'));

// V209.10: PostOnboardingInviteHandler moved to MainAppShell (requires NetworkDataProvider)
import ProviderStabilityMonitor from './components/ProviderStabilityMonitor';
import { RequestMonitorDashboard } from './components/RequestMonitorDashboard';
import { MatchNotificationProvider } from './contexts/MatchNotificationContext';
// 📊 V208.0: PostHog Analytics - Cookie Consent Banner
import { CookieConsentBanner } from './components/analytics/CookieConsentBanner';
// 🚀 V208.P.1: Defer analytics import - load only when needed
// 📊 V208.0: PostHog Analytics - Test Event (dev only) - deferred
// 🎯 V182.4: Security health check - deferred
// 🎯 V182.5: Age gate for 18+ verification
import { AgeGateScreen } from '@/components/auth/AgeGateScreen';
import { AgeVerificationService } from '@/services/auth/AgeVerificationService';
import { useAgeVerificationStatus } from '@/hooks/useAgeVerificationStatus';
import { selectAgeGateView } from '@/utils/ageVerificationSession';
import { FEATURE_FLAGS } from '@/utils/featureFlags';
import { toast } from 'sonner';
// 🎯 V183.1: Channel debug component for development
import { ChannelDebug } from '@/components/debug/ChannelDebug';
// Debug overlay for Android camera debugging
import { DebugOverlay } from '@/components/debug/DebugOverlay';
// 🎯 V185.3: Payment service initialization (unified) - deferred
import { IS_WEB, PAYMENT_PROVIDER } from '@/config/channel';
// 🎯 V210.9: Purchase success handler to prevent race condition
import { PurchaseSuccessHandler } from '@/features/subscription/components/PurchaseSuccessHandler';

// Match Age Gate shell so auth/age-check Suspense never flashes a different canvas.
const LoadingFallback = () => (
  <div className="flex h-full min-h-full w-full flex-col justify-center bg-[var(--app-bg)]">
    <span className="sr-only">Verifying account…</span>
  </div>
);

// V151.20: Removed test imports to prevent console spam and infinite loops
// Test files should be imported manually in development when needed

// V151.17: Global splash prevention - outside React to survive remounts
let globalSplashActive = false;
let globalSplashStartTime = 0;
let globalSplashCompleted = false; // V151.21: Track completion to prevent re-initialization
const INTERNAL_DASHBOARD_ENABLED =
  import.meta.env.DEV ||
  String(import.meta.env.VITE_APP_ENV ?? '').toLowerCase() === 'staging' ||
  String(import.meta.env.VITE_APP_ENV ?? '').toLowerCase() === 'development';

// Issue #7: Boot id to detect WebView restart (changes = OS killed process)
if (typeof window !== 'undefined') {
  (window as any).__blissnet_boot_id ??= Math.random().toString(36).slice(2, 10);
}

// V151.17: Simple splash state management using React Context

// V149.8: AUGMENT - Enhanced AppContent with proper auth flow and new user handling
// V150.1: CURSOR - Added GlobalProfileProvider wrapper to fix Profile page context error
const AppContent: React.FC = () => {
  // V177: Update last_active on navigation and user activity
  useLastActive();

  const sessionId = useRef(Math.random().toString(36).substring(2, 8));
  // V159.2.4: Silenced mount spam (logged every render)
  // console.log(`🔍 V151.17: AppContent mounted [${sessionId.current}] - Global splash active: ${globalSplashActive}`);

  // V157.1 PHASE 2: Mount tracking diagnostic (SAFE ADDITION - NO MODIFICATIONS)
  const mountId = useRef(Math.random().toString(36).substr(2, 9));
  const mountCount = useRef(0);
  const lastMount = useRef<number>(Date.now());
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    debugInfo('[boot] id', (window as any).__blissnet_boot_id);

    mountCount.current++;
    mountedAt.current = Date.now();
    const timeSinceLastMount = Date.now() - lastMount.current;
    lastMount.current = Date.now();

    // V159.2.4: Silenced verbose mount analysis spam
    // console.log('🔍 V157.1 Phase 2: AppContent mount analysis:', {
    //   mountId: mountId.current,
    //   mountNumber: mountCount.current,
    //   timeSinceLastMount: `${timeSinceLastMount}ms`,
    //   timestamp: new Date().toLocaleTimeString(),
    //   location: location.pathname,
    //   stackTrace: new Error().stack?.split('\n').slice(2, 5).join('\n')
    // });

    if (mountCount.current > 3) {
      console.error('🚨 V157.1: EXCESSIVE RE-MOUNTS DETECTED!', {
        count: mountCount.current,
        currentPath: location.pathname,
        recommendation: 'Check provider nesting and route stability'
      });
    }

    return () => {
      debugLog('🔍 V157.1 Phase 2: AppContent unmounting:', {
        mountId: mountId.current,
        lifespan: `${Date.now() - mountedAt.current}ms`,
        finalPath: location.pathname
      });
    };
  }, []); // Empty deps - only run on mount/unmount

  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, isReady, authResolution } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [appReady, setAppReady] = useState(false);
  const [androidAuthProbeResolved, setAndroidAuthProbeResolved] = useState(false);

  const { entitlements, refresh: refreshEntitlements } = useEntitlements(user?.id || null);
  // Launch-critical billing lifecycle guard (pending recovery + resume recheck).
  useBilling();

  // V210.5: Subscription protection - verify subscription status on app load
  useSubscriptionProtection(user, userProfile, entitlements);

  // V320.RESUME / Issue #7: visibilitychange listener - diagnostics only, NO reload
  // Store hiddenAt in sessionStorage too (survives if window var lost on WebView kill).
  useEffect(() => {
    const handleResume = () => {
      if (document.visibilityState !== 'visible') {
        const ts = Date.now();
        (window as any).__blissnet_hidden_at = ts;
        try {
          sessionStorage.setItem('__blissnet_hidden_at', String(ts));
        } catch { /* ignore */ }
        return;
      }

      const winTs = (window as any).__blissnet_hidden_at;
      const ssTs = Number(sessionStorage.getItem('__blissnet_hidden_at') || 0);
      const hiddenAt = winTs || ssTs || 0;
      const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0;

      debugInfo('[appState] resume', {
        source: 'visibilitychange',
        hiddenDurationMs: hiddenDuration,
        hadWindowTs: !!winTs,
        hadSessionTs: !!ssTs
      });

      delete (window as any).__blissnet_hidden_at;
      try {
        sessionStorage.removeItem('__blissnet_hidden_at');
      } catch { /* ignore */ }
    };

    document.addEventListener('visibilitychange', handleResume);
    return () => document.removeEventListener('visibilitychange', handleResume);
  }, []);

  // V293: Set platform class on document for CSS targeting (EARLY - before any rendering)
  // V291: Ensure platform-android class is on BOTH documentElement and body
  // V299: Add capacitor-native class for native-only CSS rules
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      document.documentElement.classList.add(`platform-${platform}`);
      document.body.classList.add(`platform-${platform}`);
      // V299: Add capacitor-native class for native-specific CSS (e.g., top gap fix)
      document.documentElement.classList.add('capacitor-native');
      document.body.classList.add('capacitor-native');
      debugLog(`🚀 V293/V299: Platform class set: platform-${platform} + capacitor-native on both documentElement and body`);
    }
  }, []);

  // V287: Platform detection diagnostics - CRITICAL for billing routing
  useEffect(() => {
    const diagnostics = {
      href: window.location.href,
      origin: window.location.origin,
      protocol: window.location.protocol,
      isNative: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
      channelConfig: { IS_WEB, PAYMENT_PROVIDER },
      timestamp: new Date().toISOString()
    };

    debugLog('🚀 V287: PLATFORM DETECTION DIAGNOSTICS:', diagnostics);

    // V287: Visual debug indicator (remove after fix verified)
    if (import.meta.env.DEV && Capacitor.isNativePlatform()) {
      const debugDiv = document.createElement('div');
      debugDiv.id = 'v287-platform-debug';
      debugDiv.style.cssText = 'position:fixed;top:0;left:0;background:lime;color:black;padding:4px 8px;z-index:99999;font-size:10px;font-family:monospace;';
      debugDiv.textContent = `NATIVE ${Capacitor.getPlatform()} | ${window.location.protocol}`;
      document.body.appendChild(debugDiv);

      // Auto-remove after 10 seconds
      setTimeout(() => {
        const el = document.getElementById('v287-platform-debug');
        if (el) el.remove();
      }, 10000);
    }

    // V287: Alert if platform detection is broken
    if (Capacitor.isNativePlatform() && window.location.href.startsWith('https://blissnet.app')) {
      console.error('🚨 V287: PLATFORM DETECTION FAILURE - Native app loading remote web!');
      console.error('🚨 V287: This will break billing routing. Check capacitor.config.ts server.url');
    }
  }, []);

  // V279.1: Initialize StatusBar on native platforms
  // V334-LAYOUT-04: overlay: true so env(safe-area-inset-top) reports real insets on A31
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener: { remove: () => Promise<void> } | null = null;
    let cancelled = false;

    // V334-LAYOUT-04: overlay: true enables env(safe-area-inset-top) on Android WebView
    // V335.4: Color/style synced by ThemeContext; here we only set overlay + platform class
    const applyStatusBarSettings = async () => {
      try {
        if (!shouldSkipNativeSystemBarManipulation()) {
          // V334-LAYOUT-04: Overlay so WebView draws under status bar; env() reports insets
          await StatusBar.setOverlaysWebView({ overlay: true });
        }

        // V293: Add 'platform-android' class to document AND body for CSS targeting
        if (Capacitor.getPlatform() === 'android') {
          document.documentElement.classList.add('platform-android');
          document.body.classList.add('platform-android');
        }

        // V335.4: Sync status bar to current theme (ThemeContext sets on mount; re-sync on resume)
        const isDark = document.documentElement.classList.contains('dark');
        void syncStatusBarToTheme(isDark);

        debugLog('[V293] ✅ StatusBar initialized - overlay: true, theme-aware');
      } catch (error) {
        console.error('[V279.1] ❌ StatusBar initialization error:', error);
      }
    };

    // V279.1: Re-apply status bar settings when app resumes
    // V291.2: Also refresh entitlements to catch subscription changes from Play Store
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (isActive && !cancelled) {
        debugInfo('[appState] resume', { source: 'appStateChange', ts: Date.now(), triggersRefresh: true });
        void applyStatusBarSettings();

        // V291.2: Refresh entitlements when app resumes
        // This catches subscription changes made in Play Store (purchase, cancel, etc.)
        if (user?.id && refreshEntitlements) {
          debugLog('[V291.2] App resumed - refreshing entitlements');
          void refreshEntitlements();
        }
      }
    };

    // Initialize status bar settings
    void applyStatusBarSettings();

    // Setup app state change listener
    const setupListener = async () => {
      try {
        const l = await CapApp.addListener('appStateChange', handleAppStateChange);
        if (!cancelled) {
          listener = l;
        } else {
          await l.remove();
        }
      } catch (error) {
        console.error('[V279.1] ❌ Failed to setup appStateChange listener:', error);
      }
    };

    void setupListener();

    // Cleanup function
    return () => {
      cancelled = true;
      if (listener) {
        void listener.remove();
      }
    };
  }, []);

  // V294: Keyboard height tracking using visualViewport API (PRIMARY) with Capacitor fallback
  // visualViewport provides the ACTUAL visible area, accounting for keyboard overlap on Android
  // V313: CRITICAL FIX - Enable --app-height tracking on Android native to prevent layout drift
  useEffect(() => {
    let baseInnerH = window.innerHeight;
    let keyboardVisible = false;
    let useVisualViewport = false;
    const vv = window.visualViewport;

    // Helper to set CSS variables
    const setVar = (name: string, val: string) => {
      document.documentElement.style.setProperty(name, val);
    };

    // Read computed safe-area bottom (app-shell already pads by this)
    // Subtracting it from kb overlap prevents a "floating" composer gap on Android.
    const getSafeBottomPx = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--sab').trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    };

    // Set app-height variable (fixes 100vh lies on mobile)
    const setAppHeight = (h: number) => {
      setVar('--app-height', `${h}px`);
      // V402: Inline style on shell overrides V324.11 ancestor-walk `height:100%!important`
      // (inline !important beats the CSS custom property cascade).
      const shell = document.querySelector('.app-shell') as HTMLElement | null;
      if (shell) {
        shell.style.setProperty('height', `${h}px`, 'important');
        shell.style.setProperty('min-height', `${h}px`, 'important');
        shell.style.setProperty('max-height', `${h}px`, 'important');
      }
    };

    // Initialize
    baseInnerH = window.innerHeight;
    setAppHeight(baseInnerH);
    setVar('--kb', '0px');
    setVar('--kbEffective', '0px');

    // V313: On Android native, ONLY track --app-height (no keyboard CSS vars)
    // Android uses adjustResize + KeyboardResize.Native for keyboard handling
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      const handleAndroidResize = () => {
        const newHeight = window.innerHeight;
        setAppHeight(newHeight);
        baseInnerH = newHeight;
        console.log('[V313] Android --app-height updated:', newHeight);
      };

      window.addEventListener('resize', handleAndroidResize);
      window.addEventListener('orientationchange', handleAndroidResize);

      let handleAndroidViewportChange: (() => void) | null = null;

      if (vv) {
        handleAndroidViewportChange = () => {
          const viewportHeight = vv.height;
          const windowHeight = window.innerHeight;
          const keyboardHeight = Math.max(0, windowHeight - viewportHeight);
          const isKeyboardOpen = keyboardHeight > 150;

          if (!isKeyboardOpen) {
            setAppHeight(windowHeight);
            baseInnerH = windowHeight;
            console.log('[V313] Android --app-height updated (keyboard closed):', windowHeight);
          }
        };

        vv.addEventListener('resize', handleAndroidViewportChange, { passive: true });
        vv.addEventListener('scroll', handleAndroidViewportChange, { passive: true });
      }

      const snapViewportAfterKeyboardHide = () => {
        const apply = () => {
          const newHeight = window.innerHeight;
          setAppHeight(newHeight);
          baseInnerH = newHeight;
          window.scrollTo(window.scrollX, window.scrollY);
        };
        apply();
        requestAnimationFrame(() => {
          apply();
          requestAnimationFrame(apply);
        });
      };

      let hide1: { remove: () => Promise<void> } | null = null;
      let hide2: { remove: () => Promise<void> } | null = null;
      Keyboard.addListener('keyboardWillHide', snapViewportAfterKeyboardHide)
        .then((l) => { hide1 = l; }).catch(() => {});
      Keyboard.addListener('keyboardDidHide', snapViewportAfterKeyboardHide)
        .then((l) => { hide2 = l; }).catch(() => {});

      return () => {
        window.removeEventListener('resize', handleAndroidResize);
        window.removeEventListener('orientationchange', handleAndroidResize);

        if (vv && handleAndroidViewportChange) {
          vv.removeEventListener('resize', handleAndroidViewportChange);
          vv.removeEventListener('scroll', handleAndroidViewportChange);
        }
        if (hide1) void hide1.remove();
        if (hide2) void hide2.remove();
      };
    }

    // V294: visualViewport-based keyboard detection (PRIMARY for Android)
    // This is the source of truth for keyboard overlap
    // Single source of truth for --kbEffective calculation
    let rafPending = false;
    const updateKeyboardEffective = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        const vv = window.visualViewport;
        const layoutInner = window.innerHeight;
        const offsetTop = vv ? Math.max(0, vv.offsetTop) : 0;
        const kb = vv
          ? Math.max(0, Math.round(layoutInner - (vv.height + offsetTop)))
          : 0;

        keyboardVisible = kb > 50; // Threshold to avoid false positives from address bar

        const isNative = Capacitor.isNativePlatform();
        const chatActive = document.documentElement.classList.contains('chat-active');
        // V403: Mobile web chat — browsers often pan the visualViewport (offsetTop > 0) so
        // innerHeight ≈ vv.height + offsetTop and kb drops to ~0 while the layout viewport
        // is still full height. That leaves keyboardVisible false → setAppHeight(innerHeight)
        // and the composer stays behind the overlay keyboard. If the chat composer is focused
        // and vv is shorter than the layout viewport, treat as keyboard-open for shell sizing.
        if (!isNative && chatActive && vv) {
          const vvH = vv.height;
          const ae = document.activeElement as HTMLElement | null;
          const composerFocused =
            !!ae &&
            (ae.classList.contains('chat-input-field') || !!ae.closest?.('.chat-composer'));
          if (composerFocused && vvH < layoutInner - 88) {
            keyboardVisible = true;
          }
        }

        setVar('--kb', `${kb}px`);
        setVar('--kbEffective', `${kb}px`);

        // V291-HOTFIX: Set visualViewport dimensions for Android layout fix
        if (vv) {
          const vvh = Math.round(vv.height);
          const vvt = Math.max(0, Math.round(vv.offsetTop));
          setVar('--vvh', `${vvh}px`);
          setVar('--vvt', `${vvt}px`);

          // Debug logging (temporary - remove after verification)
          if (keyboardVisible && document.documentElement.classList.contains('platform-android')) {
            console.log('[V291-HOTFIX VV]', { vvh, vvt, kbEffective: kb, keyboardVisible });
          }
        }

        // V291-DEBUG: Visual diagnostic overlay (TEMPORARY)
        if (keyboardVisible && document.documentElement.classList.contains('platform-android')) {
          // Create or update debug overlay
          let overlay = document.getElementById('vv-debug-overlay');
          if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'vv-debug-overlay';
            overlay.style.cssText = `
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: rgba(255, 0, 0, 0.8);
              color: white;
              font-size: 10px;
              font-family: monospace;
              padding: 4px;
              z-index: 999999;
              pointer-events: none;
              line-height: 1.2;
            `;
            document.body.appendChild(overlay);
          }

          const appShell = document.querySelector('.app-shell');
          const appShellHeight = appShell ? appShell.getBoundingClientRect().height : 0;
          const appShellTop = appShell ? appShell.getBoundingClientRect().top : 0;

          overlay.innerHTML = `
            VV: h=${vv ? vv.height.toFixed(0) : 'N/A'} top=${vv ? vv.offsetTop.toFixed(0) : 'N/A'} | 
            WIN: ${window.innerHeight} | 
            CSS: vvh=${getComputedStyle(document.documentElement).getPropertyValue('--vvh')} vvt=${getComputedStyle(document.documentElement).getPropertyValue('--vvt')} | 
            SHELL: h=${appShellHeight.toFixed(0)} top=${appShellTop.toFixed(0)} | 
            CLASSES: ${document.documentElement.classList.toString()}
          `;
        } else {
          // Remove overlay when keyboard closes
          const overlay = document.getElementById('vv-debug-overlay');
          if (overlay) overlay.remove();
        }

        // Toggle keyboard-open class on Android to allow height resize
        const platformAndroid = document.documentElement.classList.contains('platform-android');
        if (platformAndroid) {
          if (keyboardVisible) {
            document.documentElement.classList.add('keyboard-open');
          } else {
            document.documentElement.classList.remove('keyboard-open');
          }
        }

        // V401: Mobile web chat — OS keyboard overlays the layout viewport; innerHeight often stays full while
        // visualViewport shrinks. Keeping --app-height at full window leaves .app-shell tall and the composer
        // behind the keyboard. Bind shell height to the visible viewport only during chat on non-native web.
        if (!isNative && chatActive && keyboardVisible && vv) {
          // vv.height IS the visible area above the keyboard — do not add offsetTop
          const visibleH = Math.max(1, Math.round(vv.height));
          setAppHeight(visibleH);
        } else if (!keyboardVisible) {
          baseInnerH = window.innerHeight;
          setAppHeight(baseInnerH);
        }
      });
    };

    const updateFromVisualViewport = () => {
      if (!vv) return;
      updateKeyboardEffective();
    };

    // V294: Use visualViewport if available (preferred for Android accuracy)
    if (vv) {
      useVisualViewport = true;

      // Attach visualViewport listeners
      vv.addEventListener('resize', updateFromVisualViewport, { passive: true });
      vv.addEventListener('scroll', updateFromVisualViewport, { passive: true });

      // Also listen to window resize for orientation changes
      window.addEventListener('resize', updateFromVisualViewport, { passive: true });

      // Initial update
      updateKeyboardEffective();
    }

    // Capacitor keyboard listeners (FALLBACK when visualViewport unavailable)
    let show1: { remove: () => Promise<void> } | null = null;
    let show2: { remove: () => Promise<void> } | null = null;
    let hide1: { remove: () => Promise<void> } | null = null;
    let hide2: { remove: () => Promise<void> } | null = null;

    if (Capacitor.isNativePlatform() && !useVisualViewport) {
      // Fallback: use Capacitor keyboard events only if visualViewport is not available
      // Note: visualViewport is preferred, this is fallback only
      const handleKeyboardShow = (e: { keyboardHeight?: number }) => {
        // Use visualViewport calculation if available, otherwise use Capacitor value
        if (window.visualViewport) {
          updateKeyboardEffective();
        } else {
          const kbH = e.keyboardHeight ?? 0;
          const kb = Math.max(0, Math.round(kbH));
          keyboardVisible = kb > 50;
          setVar('--kb', `${kb}px`);
          setVar('--kbEffective', `${kb}px`);
        }
      };

      const handleKeyboardHide = () => {
        keyboardVisible = false;
        setVar('--kb', '0px');
        setVar('--kbEffective', '0px');
        baseInnerH = window.innerHeight;
        setAppHeight(baseInnerH);
      };

      Keyboard.addListener('keyboardWillShow', handleKeyboardShow).then((listener) => { show1 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', handleKeyboardShow).then((listener) => { show2 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardWillHide', handleKeyboardHide).then((listener) => { hide1 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', handleKeyboardHide).then((listener) => { hide2 = listener; }).catch(() => { });
    }

    // V294: For native with visualViewport, still track Capacitor events for --kb debug value
    if (Capacitor.isNativePlatform() && useVisualViewport) {
      const handleKeyboardShowDebug = (e: { keyboardHeight?: number }) => {
        const kbH = e.keyboardHeight ?? 0;
        setVar('--kb', `${kbH}px`);
        // Do NOT set --kbEffective here - visualViewport is the source of truth
      };

      const handleKeyboardHideDebug = () => {
        setVar('--kb', '0px');
        // Let visualViewport handle --kbEffective reset
      };

      Keyboard.addListener('keyboardWillShow', handleKeyboardShowDebug).then((listener) => { show1 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', handleKeyboardShowDebug).then((listener) => { show2 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardWillHide', handleKeyboardHideDebug).then((listener) => { hide1 = listener; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', handleKeyboardHideDebug).then((listener) => { hide2 = listener; }).catch(() => { });
    }

    // Non-visual-viewport resize handler (for web/desktop)
    if (!useVisualViewport) {
      const handleResize = () => {
        if (keyboardVisible) return;
        baseInnerH = window.innerHeight;
        setAppHeight(baseInnerH);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);
    }

    // Cleanup
    return () => {
      if (vv && useVisualViewport) {
        vv.removeEventListener('resize', updateFromVisualViewport);
        vv.removeEventListener('scroll', updateFromVisualViewport);
        window.removeEventListener('resize', updateFromVisualViewport);
      }
      if (!useVisualViewport) {
        window.removeEventListener('resize', () => { });
        window.removeEventListener('orientationchange', () => { });
      }
      if (show1) void show1.remove();
      if (show2) void show2.remove();
      if (hide1) void hide1.remove();
      if (hide2) void hide2.remove();
      setVar('--kb', '0px');
      setVar('--kbEffective', '0px');
      // V291-HOTFIX: Clean up visualViewport variables
      document.documentElement.style.removeProperty('--vvh');
      document.documentElement.style.removeProperty('--vvt');
    };
  }, []);

  // V213.1: Initialize Android back button with priority system
  useEffect(() => {
    initializeAndroidBackButton(navigate);
  }, [navigate]);

  // V212.2: Listen for auth success event from deep link handler and navigate via router
  useEffect(() => {
    const handleAuthSuccess = (event: CustomEvent) => {
      console.log('[V212 App] Received auth success event, navigating to /profile');
      console.log('[V212 App] User ID:', event.detail?.userId);

      // Navigate to profile using React Router
      navigate('/profile', { replace: true });
    };

    window.addEventListener('v212-auth-success', handleAuthSuccess as EventListener);

    return () => {
      window.removeEventListener('v212-auth-success', handleAuthSuccess as EventListener);
    };
  }, [navigate]);

  // V284.2: Listen for invite deep link event and navigate to /invite/:code internally
  useEffect(() => {
    const handleInviteDeepLink = (event: CustomEvent) => {
      const inviteCode = event.detail?.inviteCode;
      console.info('NATIVE_LAUNCH_ROUTE', {
        route: inviteCode ? 'invite_route' : 'other',
        action: 'deeplink_received',
      });

      if (typeof inviteCode === 'string' && inviteCode) {
        // Session consume (Go to Network) must block reinjection of the same code.
        if (!claimInviteDeepLinkNavigation(inviteCode, 'warm_open')) {
          console.info('NATIVE_LAUNCH_ROUTE', {
            route: 'invite_route',
            action: 'deeplink_skipped_consumed',
          });
          return;
        }
        console.info('NATIVE_LAUNCH_ROUTE', { route: 'invite_route', action: 'deeplink_navigating' });
        navigate(`/invite/${inviteCode}`, { replace: true });
      }
    };

    window.addEventListener('v284-invite-deeplink', handleInviteDeepLink as EventListener);

    return () => {
      window.removeEventListener('v284-invite-deeplink', handleInviteDeepLink as EventListener);
    };
  }, [navigate]);

  // Phase 2C: route notification taps through React Router once the shell is mounted.
  // V600: Forward the push payload via location.state so destination routes (e.g.
  // ChatConversation) can hydrate peer identity from sender_id without waiting on
  // a chat-row roundtrip or NetworkDataContext readiness.
  useEffect(() => {
    const handlePushNavigation = (event: CustomEvent) => {
      const path = event.detail?.path;
      const pushData = event.detail?.data ?? null;
      if (typeof path === 'string' && path.startsWith('/')) {
        localStorage.removeItem('blissnet_pending_push_path');
        localStorage.removeItem('blissnet_pending_push_data');
        navigate(path, { replace: false, state: pushData ? { pushData } : undefined });
      }
    };

    const pendingPath = localStorage.getItem('blissnet_pending_push_path');
    if (pendingPath?.startsWith('/')) {
      let pendingPushData: unknown = null;
      try {
        const raw = localStorage.getItem('blissnet_pending_push_data');
        pendingPushData = raw ? JSON.parse(raw) : null;
      } catch {
        pendingPushData = null;
      }
      localStorage.removeItem('blissnet_pending_push_path');
      localStorage.removeItem('blissnet_pending_push_data');
      navigate(pendingPath, {
        replace: false,
        state: pendingPushData ? { pushData: pendingPushData } : undefined,
      });
    }

    window.addEventListener('blissnet-push-navigation', handlePushNavigation as EventListener);

    return () => {
      window.removeEventListener('blissnet-push-navigation', handlePushNavigation as EventListener);
    };
  }, [navigate]);

  // V284.2: Handle cold start invite deep links (app launched from deep link)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const checkLaunchUrl = async () => {
      try {
        const launchUrl = await CapApp.getLaunchUrl();
        console.info('NATIVE_LAUNCH_ROUTE', {
          route: launchUrl?.url ? 'native_launch_url_present' : 'other',
        });

        if (launchUrl?.url) {
          const parsedUrl = new URL(launchUrl.url);

          // Check for invite deep links
          const isHttpsInvite = parsedUrl.hostname === 'blissnet.app' && parsedUrl.pathname.startsWith('/invite/');
          const isCustomSchemeInvite = parsedUrl.protocol === 'blissnet:' && parsedUrl.hostname === 'invite';

          if (isHttpsInvite || isCustomSchemeInvite) {
            let inviteCode: string | null = null;

            if (isHttpsInvite) {
              const match = parsedUrl.pathname.match(/^\/invite\/([^/?#]+)/);
              inviteCode = match ? match[1] : null;
            } else if (isCustomSchemeInvite) {
              inviteCode = parsedUrl.pathname.replace(/^\//, '');
            }

            if (inviteCode) {
              // Process-level claim survives effect re-runs when `navigate` identity changes;
              // session dismiss blocks reinjection after Go to Network.
              if (!claimInviteDeepLinkNavigation(inviteCode, 'cold_start')) {
                console.info('NATIVE_LAUNCH_ROUTE', {
                  route: 'invite_route',
                  action: 'cold_start_skipped_consumed',
                });
                return;
              }
              console.info('NATIVE_LAUNCH_ROUTE', {
                route: 'invite_route',
                action: 'cold_start_invite',
              });
              navigate(`/invite/${inviteCode}`, { replace: true });
            }
          }
        }
      } catch (error) {
        console.error('[V284.2 App] Error checking launch URL:', error);
      }
    };

    checkLaunchUrl();
  }, [navigate]);
  // 🎯 V182.5: Age verification — per-account DB resolve; flicker vs genuine logout in the hook.
  // Optional FEATURE_FLAGS.AGE_GATE_SESSION_BYPASS restores legacy V206.3 behavior for emergencies only.
  const {
    status: ageVerificationStatus,
    setStatusAtomic: setAgeVerificationStatusAtomic,
  } = useAgeVerificationStatus(user?.id, authResolution);

  // V181.1: Log legal routes registration - deferred
  useEffect(() => {
    // Defer non-critical logging
    requestAnimationFrame(() => {
      console.log("🎯 V181.1: Legal routes registered");
      console.log("🎯 V182.3: Subscription management modal available in Settings");
    });
  }, []);

  // 🚀 V208.P.1: Defer non-critical initialization to improve startup time
  // V209.6: Removed Paddle health check (Paddle removed)
  useEffect(() => {
    // Defer analytics test event (dev only)
    const analyticsTimer = setTimeout(() => {
      if (import.meta.env.DEV) {
        import('@/analytics').then(({ trackEvent, AnalyticsEvents }) => {
          trackEvent(AnalyticsEvents.TEST_EVENT, {
            test_property: 'hello_from_v208',
            test_timestamp: new Date().toISOString(),
          });
          console.log('📊 V208.0: Analytics test event fired (deferred)');
        }).catch(() => {
          // Fail silently - non-critical
        });
      }
    }, 2000); // 2 second delay

    return () => {
      clearTimeout(analyticsTimer);
    };
  }, []);

  const handleAgeVerified = async () => {
    if (!user?.id) return;

    console.log('🎯 V182.5: User confirmed 18+ age, marking as verified');

    const attemptMark = () => AgeVerificationService.markAsVerified(user.id);
    let success = await attemptMark();
    if (!success) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      success = await attemptMark();
    }

    if (success) {
      setAgeVerificationStatusAtomic('verified');
    } else {
      console.error('🎯 V182.5: Failed to mark user as verified after retry');
      toast.error('Could not save age verification. Please try again.');
    }
  };

  /** Underage confirmation: set denied + local 24h timestamp + best-effort DB false. No sign-out/nav. */
  const handleAgeDenied = async () => {
    if (!user?.id) return;

    console.log('🎯 V182.5: User denied 18+ age — recording local cooldown (no auto sign-out)');

    setAgeVerificationStatusAtomic('denied');

    await AgeVerificationService.markAsDenied(user.id);
  };

  /**
   * Ineligible "Return to sign in": navigate only after successful sign-out.
   * On failure: keep denied status + AgeGate mounted; throw so UI can show retry.
   */
  const handleAgeGoBack = async () => {
    if (!user?.id) return;

    console.log('🎯 V182.5: Age-gate return to sign in — sign out then /auth');

    const signedOut = await AgeVerificationService.revokeAccess(user.id);
    if (!signedOut) {
      console.error('🎯 V182.5: Sign-out failed — staying on ineligible screen');
      toast.error('Could not return to sign in. Please try again.');
      throw new Error('AGE_GATE_SIGNOUT_FAILED');
    }

    navigate('/auth', { replace: true });
  };

  // 🎨 V180.4: Light Mode Modernization - Tracking
  useEffect(() => {
    console.log("🚀 V180.4: Starting Light Mode Modernization");
    console.log("🎨 V180.4: Phase 1 complete - Theme variables updated");
    console.log("🎨 V180.4: Phase 2 complete - Core components updated");
    console.log("🎨 V180.4: Phase 3 complete - Polish applied");
    console.log("✅ V180.4: Light mode modernization complete");
    console.log("📋 V180.4: Files modified: src/index.css, src/App.tsx");
    console.log("🎯 V180.4: Ready for visual QA");
  }, []);

  // V163.8.4: Removed handleInitializationComplete - using direct routing

  // V151.21: FIXED - Handle auth state changes without infinite loop and prevent re-initialization
  useEffect(() => {
    if (!isReady) {
      return; // Wait for auth
    }

    const completeSplashReadiness = () => {
      globalSplashCompleted = true;
      setIsInitialLoad(false);
      setAppReady(true);
    };

    // If no user, ready immediately for auth page
    if (!user?.id) {
      completeSplashReadiness();
      return;
    }

    // If splash already completed, don't re-initialize
    if (globalSplashCompleted) {
      setIsInitialLoad(false);
      setAppReady(true);
      return;
    }

    // Auth ready with user — complete splash deterministically (no render-phase setState)
    console.log(`[Phase5] Completing splash for authenticated user [${sessionId.current}]`);
    completeSplashReadiness();
  }, [user, isReady]);

  // Auth bootstrap probe — all platforms (not Android-only). Prevents forever splash if getSession hangs.
  useEffect(() => {
    if (isReady) {
      setAndroidAuthProbeResolved(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        await Promise.race([
          supabase.auth.getSession(),
          new Promise<void>((resolve) => window.setTimeout(resolve, 4000)),
        ]);
      } catch {
        /* recoverable — exit spinner only when probe completes */
      }
      if (!cancelled) {
        setAndroidAuthProbeResolved(true);
        logAuthEvent('AUTH_PUBLIC_ROUTE_RENDERED', { reason: 'bootstrap-probe' });
      }
    }, 6000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isReady]);

  // V167.4: Trust score calculation removed from app load
  // V209.10: TrustScoreService import removed - only used in NetworkDataContext (lazy loaded)
  // Trust score will be calculated by NetworkDataContext after connections are loaded
  // This prevents V162 Supabase Guard violations (direct connection queries)
  useEffect(() => {
    if (!user?.id) return;
    console.log('🚀 V167.4: App load - trust score will be calculated by NetworkDataContext');
  }, [user?.id]);

  // V204: Routes that should bypass auth loading (e.g., after account deletion)
  // V333.2: Include /auth/callback so OAuth callback renders and can exchange code before auth ready
  const authBypassRoutes = ['/goodbye', '/legal/', '/support', '/help/', '/contact', '/about', '/auth/callback', '/auth-callback'];
  const isAuthBypassRoute = authBypassRoutes.some(route => location.pathname.startsWith(route));

  // V209.10 / boot: shell routing flags (computed before any early return — hooks must stay unconditional)
  const isAuthRoute = location.pathname === '/auth' || location.pathname.startsWith('/auth');
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const ageGateBypass = FEATURE_FLAGS.AGE_GATE_SESSION_BYPASS;
  const isAuthCallbackRoute =
    location.pathname.startsWith('/auth/callback') ||
    location.pathname.startsWith('/auth-callback');
  const ageGateView = selectAgeGateView({
    userId: user?.id ?? null,
    status: ageVerificationStatus,
    authResolution,
    ageGateBypass,
    isAuthCallbackRoute,
  });
  /** Full route tree: logged-out, verified, bypass+checking, or OAuth callback while age row is still resolving */
  const renderFullAppRoutes = ageGateView === 'app';
  /** After callback navigates to app routes, hold on a single route until DB age status resolves (no blank Router) */
  const renderAgeCheckingFallback = ageGateView === 'checking';
  const shouldMountHeavyProviders =
    !!user?.id &&
    ageVerificationStatus === 'verified' &&
    !isAuthRoute &&
    !isOnboardingRoute;

  // Preload MainAppShell while age is checking (after auth) — avoid Suspense waterfall on verify
  useEffect(() => {
    if (!user?.id) return;
    if (ageVerificationStatus !== 'checking' && ageVerificationStatus !== 'verified') return;
    void import('./shells/MainAppShell');
  }, [user?.id, ageVerificationStatus]);

  // Dismiss HTML #initial-loader only when boot UI is stable (MainAppShell signals itself)
  useEffect(() => {
    if (isAuthBypassRoute) {
      signalBootReady('auth-bypass');
      return;
    }
    if (!isReady && !androidAuthProbeResolved) return;
    if (!user?.id) {
      signalBootReady('logged-out');
      return;
    }
    if (ageVerificationStatus === 'not_asked' || ageVerificationStatus === 'denied') {
      signalBootReady('age-gate');
      return;
    }
    // Verified but staying on AuthShell (onboarding / auth routes) — no MainAppShell
    if (ageVerificationStatus === 'verified' && !shouldMountHeavyProviders) {
      signalBootReady('verified-light-shell');
    }
    // verified + MainAppShell: ChatList/Discover signal when paint-ready; other routes use shell fallback
  }, [
    isAuthBypassRoute,
    isReady,
    androidAuthProbeResolved,
    user?.id,
    ageVerificationStatus,
    shouldMountHeavyProviders,
  ]);

  // Auth route decision — explicit authResolution (not !isReady && !user).
  // logged_out → public immediately (even if isReady were false).
  // pending → bounded bootstrap splash (cold start; do not flash landing before hydration).
  // authenticated → app routes (auth-data loading never blocks public).
  const authRouteDecision = resolveAuthRouteDecision({
    authResolution,
    authProbeResolved: androidAuthProbeResolved,
    isAuthBypassRoute,
    hasUser: !!user?.id,
  });
  // A previously resolved gate or age-check hold wins over a transient auth
  // bootstrap. Otherwise AppBootSplash (#050508) flashes before the Age Gate.
  if (authRouteDecision === 'bootstrap-splash' && ageGateView === 'app') {
    return <AppBootSplash />;
  }

  const mainContent = (
    <div
      className="flex h-full w-full flex-col bg-[var(--app-bg)] text-foreground app-shell"
    >

      {/* 🎯 V182.5: Age gate for not_asked (question) or denied (stable ineligible — never blank) */}
      {ageGateView === 'gate' && (
        <AgeGateScreen
          onVerified={handleAgeVerified}
          onDenied={handleAgeDenied}
          onGoBack={handleAgeGoBack}
          initialDenied={ageVerificationStatus === 'denied'}
          actionsEnabled={!!user?.id}
        />
      )}

      {/* V207.24: Top navigation - desktop only - MOVED OUTSIDE age verification wrapper */}
      {user &&
        ageVerificationStatus === 'verified' &&
        !location.pathname.startsWith('/onboarding') &&
        !location.pathname.startsWith('/auth') &&
        !location.pathname.startsWith('/legal') &&
        !location.pathname.startsWith('/bar-ilan') &&
        location.pathname !== '/profile/photo' &&
        !location.pathname.match(/^\/chat\/[^/]+$/) && (
          <TopNavigation />
        )}

      {/* 🎯 V182.5: Route tree — full app vs age-check hold (keeps Router mounted after OAuth callback) */}
      {renderAgeCheckingFallback ? (
        <main className="min-h-0 flex-1 h-full w-full bg-[var(--app-bg)]" aria-busy="true" aria-live="polite">
          <Routes>
            <Route
              path="*"
              element={
                <div className="flex h-full min-h-full w-full flex-col justify-center bg-[var(--app-bg)]">
                  <span className="sr-only">Verifying account…</span>
                </div>
              }
            />
          </Routes>
        </main>
      ) : renderFullAppRoutes ? (
        <>
          {/* Main content fills space between header and bottom nav */}
          {/* V212.4: Android scroll fix - only inner content scrolls with overscroll-contain */}
          {/* V232: Profile page needs overflow-hidden like Discover to prevent scroll */}
          {/* V295: Removed h-full to allow natural height adaptation when keyboard opens */}
          {/* iPhone scroll fix: one main scroll container per screen; -webkit-overflow-scrolling: touch for native feel */}
          {(() => {
            // Scroll ownership: discover + profile + network + settings own their scroll
            // (overflow-hidden on main, JS-height inner containers handle scroll).
            // Chat list uses the tab wrapper + chatScrollRef for scroll on all platforms.
            // V404: /chat/:id — same scroll ownership as Discover/Network: .chat-messages scrolls inside
            // the grid. If main stays overflow-y-auto, min-height:auto prevents shrinking with keyboard
            // and the composer sits below the main scrollport (matches Android Chrome screenshot).
            const isChatThread = /^\/chat\/[^/]+$/.test(location.pathname);
            const pageOwnsScroll =
              location.pathname.startsWith('/discover') ||
              location.pathname.startsWith('/profile') ||
              location.pathname.startsWith('/network') ||
              location.pathname.startsWith('/settings') ||
              location.pathname === '/chat' ||
              location.pathname === '/chat/' ||
              isChatThread;

            return (
              <main
                className={`min-h-0 flex-1 ${pageOwnsScroll ? '!overflow-hidden' : 'overflow-y-auto overscroll-contain main-scroll-ios'}`}
                style={{ paddingTop: '0px' }}
              >
                <Routes>
                  {/* V149.8: PUBLIC ROUTES - No auth required */}
                  <Route path="/auth" element={<AuthenticationStep />} />
                  {/* V333.2: Dedicated OAuth callback - exchanges code, then navigates to /profile */}
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth-callback" element={<AuthCallback />} />

                  {/* ===========================
                LEGAL: aliases -> canonical
                =========================== */}
                  <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                  <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                  <Route path="/safety" element={<Navigate to="/legal/safety" replace />} />
                  <Route path="/pricing" element={<Navigate to="/legal/pricing" replace />} />
                  <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
                  <Route path="/guidelines" element={<Navigate to="/legal/guidelines" replace />} />
                  <Route path="/wedding-gift" element={<Navigate to="/legal/wedding-gift" replace />} />
                  <Route path="/subscription-management" element={<Navigate to="/legal/subscription-management" replace />} />

                  {/* ===========================
                LEGAL: canonical pages
                =========================== */}
                  <Route path="/legal/terms" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <TermsPage />
                    </Suspense>
                  } />
                  <Route path="/legal/privacy" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <PrivacyPage />
                    </Suspense>
                  } />
                  <Route path="/legal/safety" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <SafetyPage />
                    </Suspense>
                  } />
                  <Route path="/legal/pricing" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <PricingPage />
                    </Suspense>
                  } />
                  <Route path="/legal/refund" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <RefundPage />
                    </Suspense>
                  } />
                  <Route path="/legal/guidelines" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <GuidelinesPage />
                    </Suspense>
                  } />
                  <Route path="/legal/wedding-gift" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <WeddingGiftPage />
                    </Suspense>
                  } />
                  <Route path="/legal/subscription-management" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <SubscriptionManagementPage />
                    </Suspense>
                  } />
                  <Route path="/contact" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContactPage />
                    </Suspense>
                  } />

                  {/* 🚀 V208.P.1: Lazy-loaded help pages */}
                  <Route path="/help/safety" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <SafetyHelpPage />
                    </Suspense>
                  } />

                  {/* 🚀 V208.P.1: Lazy-loaded support pages */}
                  <Route path="/goodbye" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Goodbye />
                    </Suspense>
                  } />
                  <Route path="/support/delete-request" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DeleteRequest />
                    </Suspense>
                  } />
                  {/* Public support page for App Store Connect – no auth required */}
                  <Route path="/support" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <SupportPage />
                    </Suspense>
                  } />
                  {/* Public about page for Google OAuth Branding review – no auth required */}
                  <Route path="/about" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AboutPage />
                    </Suspense>
                  } />
                  <Route path="/internal" element={
                    INTERNAL_DASHBOARD_ENABLED ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <InternalDashboard />
                      </Suspense>
                    ) : (
                      <Navigate to="/profile" replace />
                    )
                  } />
                  <Route path="/settings/delete-account" element={<Navigate to="/settings" replace />} />

                  {/* Redirect /legal/terms-and-conditions to /legal/terms */}
                  <Route path="/legal/terms-and-conditions" element={<Navigate to="/legal/terms" replace />} />

                  {/* Redirect /terms-and-conditions to /legal/terms */}
                  <Route path="/terms-and-conditions" element={<Navigate to="/legal/terms" replace />} />

                  {/* Redirect /legal/privacy-policy to /legal/privacy */}
                  <Route path="/legal/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
                  {/* Redirect /privacy-policy to /legal/privacy */}
                  <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />

                  {/* Redirect /legal/refund-policy to /legal/refund */}
                  <Route path="/legal/refund-policy" element={<Navigate to="/legal/refund" replace />} />
                  {/* Redirect /refund-policy to /legal/refund */}
                  <Route path="/refund-policy" element={<Navigate to="/legal/refund" replace />} />

                  {/* Redirect /legal/community-guidelines to /legal/guidelines */}
                  <Route path="/legal/community-guidelines" element={<Navigate to="/legal/guidelines" replace />} />
                  {/* Redirect /community-guidelines to /legal/guidelines */}
                  <Route path="/community-guidelines" element={<Navigate to="/legal/guidelines" replace />} />

                  {/* Additional legal aliases */}
                  <Route path="/legal/wedding-gift-program" element={<Navigate to="/legal/wedding-gift" replace />} />
                  <Route path="/wedding-gift-program" element={<Navigate to="/legal/wedding-gift" replace />} />
                  <Route path="/legal/pricing-plans" element={<Navigate to="/legal/pricing" replace />} />
                  <Route path="/pricing-plans" element={<Navigate to="/legal/pricing" replace />} />

                  {/* Legacy auth routes redirect to /auth */}
                  <Route path="/login" element={<Navigate to="/auth" replace />} />
                  <Route path="/signin" element={<Navigate to="/auth" replace />} />
                  <Route path="/signup" element={<Navigate to="/auth" replace />} />
                  <Route path="/register" element={<Navigate to="/auth" replace />} />

                  {/* V151.14: REMOVED /splash route - splash now handled in App init only */}

                  <Route path="/onboarding" element={
                    user ? <OnboardingContainer /> : <Navigate to="/auth" replace />
                  } />

                  {/* DEFAULT ROUTE - must come before KeepAliveTabs so "/" matches first */}
                  <Route path="/" element={
                    user ? (
                      <Navigate to="/profile" replace />
                    ) : (
                      <Suspense fallback={<LoadingFallback />}>
                        <Landing />
                      </Suspense>
                    )
                  } />

                  {/* V325.29: Keep bottom-tab pages mounted to prevent img remount churn */}
                  {/* profile/photo must come before profile so it matches first */}
                  <Route path="/profile/photo" element={
                    user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <PhotoUploadPage />
                      </Suspense>
                    ) : <Navigate to="/auth" replace />
                  } />

                  <Route path="/" element={
                    user ? (
                      <KeepAliveTabs
                        fallback={<LoadingFallback />}
                        Discover={Discover}
                        Network={Network}
                        ChatList={ChatList}
                        ChatConversation={ChatConversation}
                        Profile={Profile}
                        Settings={Settings}
                      />
                    ) : <Navigate to="/auth" replace />
                  }>
                    <Route path="discover" />
                    <Route path="network" />
                    <Route path="chat" />
                    <Route path="chat/:chatId" />
                    <Route path="profile" />
                    <Route path="settings" />
                  </Route>

                  {/* 🚀 V208.P.1: Lazy-loaded test pages */}
                  <Route path="/test-legal-consent" element={
                    user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <TestLegalConsent />
                      </Suspense>
                    ) : <Navigate to="/auth" replace />
                  } />

                  {/* 🚀 V208.P.1: Lazy-loaded verification routes */}
                  <Route path="/verification/success" element={
                    user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <VerificationSuccess />
                      </Suspense>
                    ) : <Navigate to="/auth" replace />
                  } />

                  <Route path="/verification/cancel" element={
                    user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <VerificationCancel />
                      </Suspense>
                    ) : <Navigate to="/auth" replace />
                  } />

                  <Route path="/test/dialog" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <TestDialog />
                    </Suspense>
                  } />

                  <Route path="/test/profile-direct" element={
                    user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <ProfileDirect />
                      </Suspense>
                    ) : <Navigate to="/auth" replace />
                  } />

                  {/* Invite route behavior:
                      - Native app: always run canonical invite flow
                      - Web + unauthenticated: show fallback invite page
                      - Web + authenticated: run canonical invite flow */}
                  <Route path="/invite/:inviteCode" element={
                    !Capacitor.isNativePlatform() && !user ? (
                      <Suspense fallback={<LoadingFallback />}>
                        <InviteRedirect />
                      </Suspense>
                    ) : (
                      <Suspense fallback={<LoadingFallback />}>
                        <InviteAccept />
                      </Suspense>
                    )
                  } />

                  {/* Bar-Ilan Founding leaderboard — no gating; page handles auth loading and sign-in prompt */}
                  <Route path="/bar-ilan/dashboard" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <BarIlanDashboardPage />
                    </Suspense>
                  } />

                  {/* Bar-Ilan Founding admin panel - hardcoded email allowlist + RLS */}
                  <Route
                    path="/bar-ilan/admin"
                    element={
                      user?.email === 'omer@blissnet.app'
                        ? (
                          <Suspense fallback={<LoadingFallback />}>
                            <BarIlanAdminPage />
                          </Suspense>
                        ) : (
                          <Navigate to="/bar-ilan/dashboard" replace />
                        )
                    }
                  />

                  {/* CATCH ALL
                 - Authenticated: send to profile
                 - Unauthenticated: send to public landing
              */}
                  <Route path="*" element={
                    user ? <Navigate to="/profile" replace /> : <Navigate to="/" replace />
                  } />
                </Routes>
              </main>
            );
          })()}
        </>
      ) : null}

      {/* V207.24: Bottom nav - MOVED OUTSIDE age verification wrapper */}
      {/* Bottom nav - static on desktop (flex), fixed on mobile (overlay) */}
      {user &&
        ageVerificationStatus === 'verified' &&
        !location.pathname.startsWith('/onboarding') &&
        !location.pathname.startsWith('/auth') &&
        !location.pathname.startsWith('/legal') &&
        !location.pathname.startsWith('/bar-ilan') &&
        location.pathname !== '/profile/photo' &&
        !location.pathname.match(/^\/chat\/[^/]+$/) && (
          <>
            <BottomNavigation chatCount={0} />
            {/* V335.3: Mobile spacer - match nav height (60px + safe-area) to prevent content behind fixed nav */}
            <div className="md:hidden" style={{ height: 'calc(60px + env(safe-area-inset-bottom, 0px))' }} />
          </>
        )}

      {/* Dev components disabled per production requirements */}
      {/* {process.env.NODE_ENV === 'development' && (
        <>
          <ProviderStabilityMonitor />
          <RequestMonitorDashboard />
        </>
      )} */}

      {/* 🎯 V183.1: Channel debug disabled */}
      {/* {import.meta.env.DEV && <ChannelDebug />} */}

      {/* 📊 V208.0: PostHog Analytics - Cookie Consent Banner (web only) */}
      <CookieConsentBanner />

      {/* 🎯 V210.9: Purchase success handler - prevents race condition */}
      <PurchaseSuccessHandler />

      {/* Debug overlay (enabled via ?debug=1) */}
      <DebugOverlay />
    </div>
  );

  // V209.10: Conditionally wrap with appropriate shell (lazy loaded).
  // Never use mainContent as Suspense fallback — that mounts routes without providers, then remounts.
  const wrappedContent = shouldMountHeavyProviders ? (
    <Suspense fallback={<AppBootSplash />}>
      <MainAppShell>
        {mainContent}
      </MainAppShell>
    </Suspense>
  ) : (
    <AuthShell>
      {mainContent}
    </AuthShell>
  );

  // V209.10: PostOnboardingInviteHandler moved to MainAppShell (requires NetworkDataProvider)
  // It's only rendered when MainAppShell is mounted (authenticated routes)
  return (
    <LoadingProvider>
      <NavigationGuard>
        <GlobalProfileProvider>
          <MatchNotificationProvider>
            {wrappedContent}
          </MatchNotificationProvider>
        </GlobalProfileProvider>
      </NavigationGuard>
    </LoadingProvider>
  );
};

const DocumentDirectionSync: React.FC = () => {
  useDocumentDirection();
  return null;
};

const NetworkRecoveryCoordinator: React.FC = () => {
  const { user } = useAuth();
  useNetworkRecovery({
    expectedUserId: user?.id ?? null,
    onRecovered: recoverMessagesChannel,
  });
  return null;
};

// V149.8: Main App component with providers
// V209.10: Only mount heavy providers (NetworkDataProvider, MessagesDataProvider) on authenticated routes
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NetworkRecoveryCoordinator />
        <PreferenceSyncOnAuth />
        <UserProvider>
          <PhotoProvider>
            <DocumentDirectionSync />
            <AppContent />
          </PhotoProvider>
        </UserProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
